import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';

const FeedbackModal = ({ isOpen, onClose, onSubmit, rating, isSubmitting }) => {
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFeedback('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (feedback.trim().length > 0) {
      onSubmit(feedback);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getRatingIcon = () => {
    if (rating === 'thumbs_up') {
      return <ThumbsUp className="w-5 h-5 text-emerald-600" />;
    } else if (rating === 'thumbs_down') {
      return <ThumbsDown className="w-5 h-5 text-red-600" />;
    }
    return null;
  };

  const getRatingText = () => {
    if (rating === 'thumbs_up') {
      return 'Thumbs Up';
    } else if (rating === 'thumbs_down') {
      return 'Thumbs Down';
    }
    return 'Rating';
  };

  const getRatingColor = () => {
    if (rating === 'thumbs_up') {
      return 'text-emerald-600';
    } else if (rating === 'thumbs_down') {
      return 'text-red-600';
    }
    return 'text-slate-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            {getRatingIcon()}
            <h3 className="text-lg font-semibold text-slate-800">
              {getRatingText()} Feedback
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <p className="text-sm text-slate-600 mb-3">
              Please provide feedback about this AI response. Your input helps improve the quality of our AI assistant.
            </p>
            
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your feedback here... (1-300 characters)"
              className="min-h-[100px] resize-none"
              disabled={isSubmitting}
              maxLength={300}
            />
            
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-slate-500">
                Plain text only (no HTML or markdown)
              </p>
              <p className={`text-xs ${feedback.length > 300 ? 'text-red-500' : 'text-slate-500'}`}>
                {feedback.length}/300
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || feedback.trim().length === 0 || feedback.length > 300}
              className={`${getRatingColor()} border-current hover:bg-opacity-10`}
            >
              {isSubmitting ? 'Submitting...' : `Submit ${getRatingText()}`}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-4 pb-4">
          <p className="text-xs text-slate-500">
            Press Ctrl+Enter (or Cmd+Enter on Mac) to submit quickly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
